/* Shopify-native catalog and cart. Shopify is the source of prices and quantities. */
(() => {
  'use strict';
  if (window.GoliCommerce) { window.GoliCommerce.init(document); return; }
  const config = window.GoliTheme || {};
  const root = `/${String(config.routesRoot || '/').replace(/^\/+|\/+$/g, '')}/`.replace('//', '/');
  const endpoint = path => root + String(path).replace(/^\//, '');
  const states = new WeakMap();
  const pendingForms = new WeakSet();
  const carouselStates = new WeakMap();
  const carousels = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let queue = Promise.resolve();
  let mutations = 0;
  let mutationFailures = 0;
  let checkoutBusy = false;
  let checkoutHandler = null;
  let checkoutTask;
  const checkoutEntryMode = () => config.checkoutEntryMode ?? document.body.dataset.checkoutEntryMode ?? (document.body.dataset.checkoutPreview === 'true' || config.checkoutPreview !== undefined && config.checkoutPreview?.enabled !== false ? 'preview' : 'native');
  const checkoutPreviewRequested = () => {
    const mode = checkoutEntryMode();
    if (mode === 'native') return false;
    // A preview setting can survive publishing. An explicitly public theme
    // keeps its native entry unless production was separately selected.
    return !(mode === 'preview' && window.Shopify?.theme?.role === 'main' && !window.Shopify?.designMode && !config.checkoutPreview?.designMode);
  };
  let cartCurrency = config.currency || document.documentElement.dataset.currency || 'USD';
  let notificationTimer;
  const serialize = task => {
    const result = queue.then(task, task);
    queue = result.catch(() => {});
    return result;
  };
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const safeUrl = value => {
    try { const url = new URL(String(value || ''), window.location.href); return /^https?:$/.test(url.protocol) ? url.href : '#'; }
    catch { return '#'; }
  };
  function money(cents, currency = cartCurrency) {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents || 0) / 100); }
    catch { return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`; }
  }
  function displayPrice(node, amount, units, formatted) {
    if (!node) return;
    node.textContent = units ? money(amount / units) : formatted || money(amount);
    if (units) {
      const suffix = document.createElement('span'); suffix.textContent = ' each'; node.append(suffix);
    }
  }
  function notify(message) {
    let box = document.getElementById('goli-notification');
    if (!box) { box = document.createElement('div'); box.id = 'goli-notification'; box.className = 'goli-notification'; box.setAttribute('role', 'status'); box.setAttribute('aria-live', 'polite'); document.body.append(box); }
    box.textContent = String(message || 'Something went wrong. Please try again.');
    box.hidden = false;
    clearTimeout(notificationTimer);
    notificationTimer = setTimeout(() => { box.hidden = true; }, 7000);
  }
  async function request(path, data) {
    const response = await fetch(endpoint(path), { method: data ? 'POST' : 'GET', credentials: 'same-origin', headers: { Accept: 'application/json', ...(data ? { 'Content-Type': 'application/json' } : {}) }, ...(data ? { body: JSON.stringify(data) } : {}) });
    let body;
    try { body = await response.json(); } catch { throw new Error('The shop returned an invalid response. Please refresh and check your cart.'); }
    if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : typeof body.message === 'string' ? body.message : 'Your cart could not be updated. Please try again.');
    return body;
  }
  function lockCart() {
    const busy = mutations > 0 || checkoutBusy;
    document.querySelectorAll('[data-cart-view]').forEach(view => {
      view.setAttribute('aria-busy', String(busy));
      view.querySelectorAll('button,input').forEach(control => { control.disabled = busy; });
    });
  }
  async function mutate(task) {
    mutations++; lockCart();
    try { return await serialize(task); }
    catch (error) { mutationFailures++; throw error; }
    finally { mutations--; lockCart(); }
  }
  function beginCheckout(form) {
    if (checkoutBusy) return checkoutTask;
    checkoutBusy = true; lockCart();
    const failedBefore = mutationFailures;
    const view = form.closest('[data-cart-view]');
    notify('Preparing your secure checkout…');
    checkoutTask = serialize(async () => {
      if (mutationFailures !== failedBefore) throw new Error('Your cart update could not be confirmed. Review the cart and select checkout again.');
      if (typeof checkoutHandler !== 'function') throw new Error('The checkout preview could not be loaded. Please refresh this page. Your cart has been kept.');
      const current = view?.querySelector('.goli-cart-form');
      const quantities = Array.from(current?.querySelectorAll('[data-cart-quantity]') || [], input => Number(input.value));
      if (!view?.isConnected || !quantities.length || quantities.length > 20 || quantities.some(value => !Number.isSafeInteger(value) || value < 1 || value > 20)) throw new Error('Please finish updating your cart, with up to 20 products and quantity between 1 and 20.');
      return checkoutHandler(quantities);
    }).catch(error => { notify(typeof error?.message === 'string' ? error.message : 'Checkout could not be confirmed. Please try again with the same cart.'); })
      .finally(() => { checkoutBusy = false; checkoutTask = undefined; lockCart(); });
    return checkoutTask;
  }
  function quantity(value) {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new Error('Enter a whole quantity of zero or more.');
    return number;
  }
  function selection(product, state) {
    const variant = product.variants.find(item => String(item.id) === String(state.variantId));
    if (!variant) throw new Error('Choose an available variant.');
    const plans = variant.plans || [];
    const required = Boolean(product.requiresPlan || variant.requiresPlan);
    const mode = required ? 'subscription' : state.mode;
    const plan = mode === 'subscription' ? plans.find(item => String(item.id) === String(state.planId)) : null;
    return { variant, plans, required, mode, plan, valid: Boolean(variant.available && (mode !== 'subscription' || plan)) };
  }
  function addPayload(product, state) {
    const chosen = selection(product, state);
    if (!chosen.valid) throw new Error(chosen.variant.available ? 'Choose an available subscription delivery option.' : 'This variant is sold out.');
    return { items: [{ id: chosen.variant.id, quantity: 1, ...(chosen.plan ? { selling_plan: chosen.plan.id } : {}) }] };
  }
  function syncProductUrl(state) {
    if (!document.body.classList.contains('template-product')) return;
    let chosen; try { chosen = selection(state.product, state); } catch { return; }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(chosen.variant.id));
    if (chosen.plan) url.searchParams.set('selling_plan', String(chosen.plan.id)); else url.searchParams.delete('selling_plan');
    window.history.replaceState({}, '', url);
  }
  function setupProduct(card) {
    if (states.has(card)) return states.get(card);
    let product;
    try { product = JSON.parse(card.querySelector('[data-product-json]')?.textContent || '{}'); } catch { return null; }
    if (!Array.isArray(product.variants) || !product.variants.length) return null;
    const initialId = card.querySelector('[name="id"]')?.value?.trim();
    // A linked variant may sit outside Liquid's first 250 variants. Never
    // silently buy another SKU if its server-selected ID is absent in JSON.
    const variant = initialId ? product.variants.find(item => String(item.id) === initialId) : product.variants.find(item => item.available) || product.variants[0];
    const initialPlan = variant?.plans?.find(plan => String(plan.id) === String(product.initialSellingPlanId));
    const state = { product, variantId: initialId || variant?.id, mode: initialPlan || product.requiresPlan || variant?.requiresPlan ? 'subscription' : 'one-time', planId: initialPlan?.id || variant?.plans?.[0]?.id || null };
    states.set(card, state); updateProduct(card, state); return state;
  }
  function updateProduct(card, state) {
    let chosen;
    try { chosen = selection(state.product, state); }
    catch {
      const add = card.querySelector('[data-product-add]'); if (add) { add.disabled = true; add.textContent = 'CHOOSE A VARIANT'; }
      let error = card.querySelector('[data-product-error]');
      if (!error) { error = document.createElement('p'); error.className = 'goli-product-error'; error.dataset.productError = ''; error.setAttribute('role', 'status'); card.querySelector('[data-product-form]').append(error); }
      error.textContent = 'Your selected variant could not be loaded. Choose a listed variant or reload its direct link before adding to cart.'; error.hidden = false;
      return;
    }
    const error = card.querySelector('[data-product-error]'); if (error) error.hidden = true;
    const { variant, plans, required } = chosen;
    if (!plans.some(plan => String(plan.id) === String(state.planId))) state.planId = plans[0]?.id || null;
    state.mode = required ? 'subscription' : plans.length ? state.mode : 'one-time';
    const current = selection(state.product, state);
    const form = card.querySelector('[data-product-form]');
    form.querySelector('[name="id"]').value = String(variant.id);
    const planInput = form.querySelector('[name="selling_plan"]');
    planInput.disabled = !current.plan;
    planInput.value = current.plan ? String(current.plan.id) : '';
    card.querySelectorAll('[data-variant]').forEach(button => { const active = String(button.dataset.variant) === String(variant.id); button.classList.toggle('is-selected', active); button.setAttribute('aria-pressed', String(active)); });
    card.querySelectorAll('[data-mode]').forEach(button => {
      button.hidden = button.dataset.mode === 'subscription' ? plans.length === 0 : required;
      const active = button.dataset.mode === state.mode;
      button.classList.toggle('is-selected', active); button.setAttribute('aria-pressed', String(active));
    });
    const units = Number.isSafeInteger(variant.packUnits) && variant.packUnits > 0 && variant.packUnits <= 1000 ? variant.packUnits : null;
    card.dataset.purchaseModes = plans.length ? 'subscription' : 'single';
    displayPrice(card.querySelector('[data-onetime-price]'), variant.price, units, variant.priceText);
    const total = card.querySelector('[data-onetime-total]'); if (total) total.textContent = variant.priceText || money(variant.price);
    const compare = card.querySelector('[data-compare-price]'); if (compare) { compare.textContent = variant.compareText || money(variant.comparePrice); compare.hidden = !(variant.comparePrice > variant.price); }
    const plan = plans.find(item => String(item.id) === String(state.planId));
    const subscriptionPrice = card.querySelector('[data-subscription-price]');
    if (plan) displayPrice(subscriptionPrice, plan.price, units, plan.priceText); else if (subscriptionPrice) subscriptionPrice.textContent = '';
    const subscriptionTotal = card.querySelector('[data-subscription-total]'); if (subscriptionTotal) subscriptionTotal.textContent = plan ? plan.priceText || money(plan.price) : '';
    const subscriptionCompare = card.querySelector('[data-subscription-compare]'); if (subscriptionCompare) { subscriptionCompare.textContent = variant.compareText || money(variant.comparePrice); subscriptionCompare.hidden = !plan || !(variant.comparePrice > plan.price); }
    const subscriptionNote = card.querySelector('[data-subscription-note]');
    const subscriptionSaving = plan && variant.price > 0 ? Math.floor((variant.price - plan.price) * 100 / variant.price) : 0;
    if (subscriptionNote) { subscriptionNote.textContent = subscriptionSaving > 0 ? `${subscriptionSaving}% OFF` : ''; subscriptionNote.hidden = subscriptionSaving <= 0; }
    const discount = card.querySelector('[data-discount-badge]');
    const currentPrice = current.plan?.price ?? variant.price;
    const saving = variant.comparePrice > currentPrice && currentPrice >= 0 ? Math.floor((variant.comparePrice - currentPrice) * 100 / variant.comparePrice) : 0;
    if (discount) { discount.textContent = saving > 0 ? `${saving}%\nOFF` : ''; discount.hidden = saving <= 0; }
    const subscriptionLabel = card.querySelector('[data-subscription-label]'); if (subscriptionLabel) subscriptionLabel.textContent = plan && plan.price < variant.price ? 'Subscribe & Save' : 'Subscribe';
    const subscriptionName = card.querySelector('[data-subscription-name]'); if (subscriptionName) subscriptionName.textContent = plan?.name || '';
    const planSelect = card.querySelector('[data-plan-select]');
    if (planSelect) {
      planSelect.innerHTML = plans.map(item => `<option value="${escape(item.id)}"${String(item.id) === String(state.planId) ? ' selected' : ''}>${escape(item.name)} — ${escape(item.priceText || money(item.price))}</option>`).join('');
      planSelect.closest('label').hidden = !(state.mode === 'subscription' && plans.length > 1);
    }
    let image = card.querySelector('.go-pack-image');
    if (image && variant.image) {
      if (image.tagName !== 'IMG') { const replacement = document.createElement('img'); replacement.className = 'go-pack-image'; replacement.width = 720; replacement.height = 720; replacement.loading = 'lazy'; image.replaceWith(replacement); image = replacement; }
      image.src = safeUrl(variant.image); image.removeAttribute('srcset'); image.alt = `${state.product.title} — ${variant.title}`;
    }
    const add = card.querySelector('[data-product-add]');
    if (add) { add.disabled = !current.valid || pendingForms.has(form); add.textContent = !variant.available ? 'SOLD OUT' : !current.valid ? 'PLAN UNAVAILABLE' : 'ADD TO CART'; }
    const status = card.querySelector('[data-product-status]'); if (status) status.textContent = `${variant.title}, ${current.plan ? current.plan.name : 'one-time purchase'}, ${current.plan?.priceText || variant.priceText || money(current.plan?.price ?? variant.price)}`;
  }
  function renderCart(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Unable to read your cart. Please refresh.');
    cartCurrency = cart.currency || cartCurrency;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(cart.item_count); node.hidden = cart.item_count === 0; });
    const html = cart.items.length ? `<form action="${escape(endpoint('cart'))}" method="post" class="goli-cart-form"><div class="goli-cart-lines">${cart.items.map((item, index) => {
      const plan = item.selling_plan_allocation?.selling_plan?.name;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escape(item.variant_title)}</p>` : '';
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== null && value !== '').map(([key, value]) => `<p>${escape(key)}: ${escape(value)}</p>`).join('');
      return `<article class="goli-cart-line" data-cart-line="${escape(item.key)}"><a class="goli-cart-image" href="${escape(safeUrl(item.url))}">${item.image ? `<img src="${escape(safeUrl(item.image))}" alt="${escape(item.product_title)}" width="120" height="120" loading="lazy">` : ''}</a><div class="goli-cart-info"><h2><a href="${escape(safeUrl(item.url))}">${escape(item.product_title)}</a></h2>${variantTitle}${plan ? `<p>${escape(plan)}</p>` : ''}${properties}<strong>${escape(money(item.final_line_price))}</strong><div class="goli-line-controls"><div class="goli-quantity"><button type="button" data-cart-step="-1" aria-label="Decrease quantity of ${escape(item.product_title)}">−</button><input type="number" name="updates[]" min="0" value="${item.quantity}" inputmode="numeric" aria-label="Quantity for ${escape(item.product_title)}" data-cart-quantity><button type="button" data-cart-step="1" aria-label="Increase quantity of ${escape(item.product_title)}">+</button></div><button type="button" class="goli-remove" data-cart-remove>Remove</button></div></div></article>`;
    }).join('')}</div><aside class="goli-cart-summary"><div><span>Subtotal</span><strong>${escape(money(cart.total_price))} ${escape(cartCurrency)}</strong></div>${(cart.cart_level_discount_applications || []).map(item => `<p>${escape(item.title)} −${escape(money(item.total_allocated_amount))}</p>`).join('')}<p>${cart.taxes_included ? 'Taxes included. ' : ''}Shipping and final taxes calculated at checkout.</p><button type="submit" name="checkout" class="go-add-to-cart">CHECKOUT</button><a href="${escape(endpoint('collections/all'))}">Continue shopping</a></aside></form>` : `<div class="goli-empty"><p>Your cart is empty.</p><a href="${escape(endpoint('collections/all'))}">Continue shopping</a></div>`;
    document.querySelectorAll('[data-cart-view]').forEach(view => { view.innerHTML = html; });
    lockCart();
    document.dispatchEvent(new CustomEvent('goli:cart-updated', { detail: cart }));
    return cart;
  }
  async function readCart() { return renderCart(await request('cart.js')); }
  function showDialog(dialog) {
    if (!dialog || dialog.open) return;
    document.querySelectorAll('dialog[open]').forEach(open => open.close());
    dialog.showModal(); document.documentElement.classList.add('goli-dialog-open');
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-goli-product]').forEach(setupProduct);
    for (const carousel of carousels) if (!carousel.element.isConnected) carousel.destroy();
    const selector = '[data-gs-carousel],.discover-section';
    if (scope.matches?.(selector)) setupCarousel(scope);
    scope.querySelectorAll(selector).forEach(setupCarousel);
  }
  function setupCarousel(element) {
    const existing = carouselStates.get(element);
    if (existing) { existing.measure(); return existing; }
    const discover = element.matches('.discover-section');
    const track = element.querySelector(discover ? '.discover-track' : '[data-gs-track]');
    const dots = element.querySelector(discover ? '.discover-dots' : '[data-gs-dots]');
    if (!track || !dots) return;
    const autoplay = element.dataset.gsCarousel === 'features' && element.dataset.gsAutoplay === 'true';
    const duration = Math.max(2000, Math.min(30000, Number(element.dataset.gsDuration) || 6000));
    const loop = discover || autoplay;
    const snap = track.style.scrollSnapType;
    let targets = [0];
    let frame = null;
    let segment;
    let interactionTimer;
    let interacting = false;
    let pointer = false;
    let hovered = false;
    let focused = element.contains(document.activeElement);
    let userPaused = false;
    let toggle;
    const paused = () => userPaused || hovered || focused || pointer || interacting || document.hidden || reducedMotion.matches;
    function activeIndex() { return targets.reduce((best, value, index) => Math.abs(value - track.scrollLeft) < Math.abs(targets[best] - track.scrollLeft) ? index : best, 0); }
    function reflect() {
      const active = activeIndex();
      Array.from(dots.children).forEach((dot, index) => { dot.classList.toggle('is-active', index === active); dot.setAttribute('aria-current', String(index === active)); });
      const activeDot = dots.children[active];
      if (activeDot) dots.scrollLeft = activeDot.offsetLeft - dots.clientWidth / 2 + activeDot.clientWidth / 2;
      const previous = element.querySelector(discover ? '[data-discover-step="-1"]' : '[data-gs-previous]');
      const next = element.querySelector(discover ? '[data-discover-step="1"]' : '[data-gs-next]');
      if (previous) previous.disabled = targets.length < 2 || !loop && track.scrollLeft <= 2;
      if (next) next.disabled = targets.length < 2 || !loop && track.scrollLeft >= targets.at(-1) - 2;
    }
    function stop() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null; segment = undefined; track.style.scrollSnapType = snap;
    }
    function resume() {
      if (!autoplay) return;
      if (toggle) {
        toggle.textContent = userPaused ? 'Resume slideshow' : 'Pause slideshow';
        toggle.setAttribute('aria-pressed', String(userPaused));
        toggle.disabled = reducedMotion.matches || targets.length < 2;
      }
      if (!element.isConnected || paused() || targets.length < 2) { stop(); return; }
      if (frame === null) { track.style.scrollSnapType = 'none'; frame = requestAnimationFrame(tick); }
    }
    function tick(now) {
      frame = null;
      if (!element.isConnected) { controller.destroy(); return; }
      if (paused() || targets.length < 2) { stop(); return; }
      if (!segment) {
        let from = track.scrollLeft;
        if (from >= targets.at(-1) - 1) { from = targets[0]; track.scrollLeft = from; }
        const to = targets.find(value => value > from + 1) ?? targets.at(-1);
        const stride = targets.length > 1 ? targets[1] - targets[0] : 1;
        segment = { from, to, start: now, duration: Math.max(300, duration * Math.abs(to - from) / Math.max(1, stride)) };
      }
      const progress = Math.min(1, Math.max(0, (now - segment.start) / segment.duration));
      track.scrollLeft = segment.from + (segment.to - segment.from) * progress;
      reflect();
      if (progress === 1) segment = undefined;
      frame = requestAnimationFrame(tick);
    }
    function measure() {
      stop();
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const slides = Array.from(track.children);
      if (discover) {
        const stride = slides.length > 1 ? slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left : track.clientWidth;
        const gap = slides[0] ? Math.max(0, stride - slides[0].getBoundingClientRect().width) : 0;
        const step = Math.max(1, stride > 0 ? stride * Math.max(1, Math.floor((track.clientWidth + gap) / stride)) : track.clientWidth);
        targets = Array.from({ length: Math.ceil(max / step) + 1 }, (_, index) => Math.min(max, index * step));
      } else {
        const bounds = track.getBoundingClientRect();
        targets = slides.map(slide => { const rect = slide.getBoundingClientRect(); return Math.min(max, Math.max(0, track.scrollLeft + rect.left - bounds.left - (track.clientWidth - rect.width) / 2)); });
      }
      targets = [...new Set(targets.map(value => Math.round(value)))];
      if (!targets.length) targets = [0];
      if (dots.childElementCount !== targets.length) {
        dots.replaceChildren();
        targets.forEach((_, index) => { const button = document.createElement('button'); button.type = 'button'; if (discover) button.dataset.discoverPage = String(index); else { button.className = 'gs-carousel-dot'; button.dataset.carouselIndex = String(index); } button.setAttribute('aria-label', `Go to ${discover ? 'product page' : 'slide'} ${index + 1}`); dots.append(button); });
      }
      track.scrollLeft = Math.min(max, Math.max(0, track.scrollLeft));
      reflect(); resume();
    }
    function interact(milliseconds = 1200) {
      clearTimeout(interactionTimer); interacting = true; stop();
      interactionTimer = setTimeout(() => { interacting = false; resume(); }, milliseconds);
    }
    const controller = {
      element, measure, reflect, resume,
      go(index) {
        interact(400);
        const target = loop ? (index % targets.length + targets.length) % targets.length : Math.max(0, Math.min(targets.length - 1, index));
        track.scrollTo({ left: targets[target], behavior: reducedMotion.matches ? 'instant' : 'smooth' }); reflect();
      },
      step(delta) { controller.go(activeIndex() + delta); },
      hover(value) { hovered = value; resume(); },
      focus(value) { focused = value; resume(); },
      pointer(value) { if (value === pointer) return; pointer = value; if (!value) interact(); else stop(); },
      interact,
      toggle() { userPaused = !userPaused; if (!userPaused) focused = false; resume(); },
      destroy() { stop(); clearTimeout(interactionTimer); carouselStates.delete(element); carousels.delete(controller); }
    };
    if (autoplay) {
      toggle = element.querySelector('[data-gs-toggle-autoplay]');
      if (!toggle) { toggle = document.createElement('button'); toggle.type = 'button'; toggle.className = 'gs-autoplay-toggle'; toggle.dataset.gsToggleAutoplay = ''; element.querySelector('.gs-carousel-controls')?.append(toggle); }
    }
    carouselStates.set(element, controller); carousels.add(controller); measure(); return controller;
  }
  document.addEventListener('click', event => {
    const target = event.target.closest('button,a,[data-open]'); if (!target) return;
    const open = target.closest('[data-open]');
    if (open) { const name = open.dataset.open; const dialog = document.getElementById(name === 'menu' ? 'menu-dialog' : name === 'cart' ? 'cart-dialog' : `${name}-dialog`); if (dialog) { event.preventDefault(); showDialog(dialog); if (name === 'cart') serialize(readCart).catch(error => notify(error.message)); } return; }
    if (target.matches('[data-close-dialog]')) { target.closest('dialog')?.close(); return; }
    const card = target.closest('[data-goli-product]');
    if (card) {
      const state = setupProduct(card); if (!state) return;
      if (target.hasAttribute('data-variant')) { state.variantId = target.dataset.variant; updateProduct(card, state); syncProductUrl(state); return; }
      if (target.hasAttribute('data-mode')) { state.mode = target.dataset.mode; updateProduct(card, state); syncProductUrl(state); return; }
    }
    if (target.matches('[data-cart-step],[data-cart-remove]')) {
      if (mutations || checkoutBusy) return;
      const line = target.closest('[data-cart-line]'); const input = line?.querySelector('[data-cart-quantity]'); if (!input) return;
      const next = target.hasAttribute('data-cart-remove') ? 0 : Math.max(0, Number(input.value) + Number(target.dataset.cartStep));
      changeLine(line.dataset.cartLine, next); return;
    }
    const carousel = target.closest('[data-gs-carousel]');
    if (carousel && target.matches('[data-gs-previous],[data-gs-next],[data-carousel-index],[data-gs-toggle-autoplay]')) {
      const controller = carouselStates.get(carousel) || setupCarousel(carousel); if (!controller) return;
      if (target.hasAttribute('data-gs-toggle-autoplay')) controller.toggle();
      else if (target.dataset.carouselIndex !== undefined) controller.go(Number(target.dataset.carouselIndex));
      else controller.step(target.hasAttribute('data-gs-previous') ? -1 : 1);
      return;
    }
    if (target.matches('[data-discover-step],[data-discover-page]')) {
      const element = target.closest('.discover-section'); if (!element) return;
      const controller = carouselStates.get(element) || setupCarousel(element); if (!controller) return;
      if (target.dataset.discoverPage !== undefined) controller.go(Number(target.dataset.discoverPage)); else controller.step(Number(target.dataset.discoverStep));
    }
  });
  async function changeLine(key, next) {
    if (checkoutBusy) { notify('Your checkout is being confirmed. Wait before changing the cart.'); return; }
    try { quantity(next); await mutate(async () => { renderCart(await request('cart/change.js', { id: key, quantity: next })); }); }
    catch (error) { notify(error.message); try { await serialize(readCart); } catch { /* Keep the last server-confirmed totals visible. */ } }
  }
  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-plan-select]')) { const card = target.closest('[data-goli-product]'); const state = setupProduct(card); if (state) { state.planId = target.value; updateProduct(card, state); syncProductUrl(state); } }
    if (target.matches('[data-cart-quantity]') && !mutations && !checkoutBusy) changeLine(target.closest('[data-cart-line]').dataset.cartLine, Number(target.value));
  });
  document.addEventListener('submit', event => {
    if (!event.target.matches('.goli-cart-form') || !checkoutPreviewRequested()) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void beginCheckout(event.target);
  }, true);
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('[data-product-form]')) return;
    event.preventDefault();
    if (checkoutBusy) { notify('Your checkout is being confirmed. Wait before changing the cart.'); return; }
    if (pendingForms.has(form)) return;
    const card = form.closest('[data-goli-product]'); const state = setupProduct(card); if (!state) return;
    let payload; try { payload = addPayload(state.product, state); } catch (error) { notify(error.message); return; }
    pendingForms.add(form); form.setAttribute('aria-busy', 'true'); updateProduct(card, state);
    try { await mutate(async () => { await request('cart/add.js', payload); await readCart(); }); showDialog(document.getElementById('cart-dialog')); }
    catch (error) { notify(error.message); }
    finally { pendingForms.delete(form); form.removeAttribute('aria-busy'); updateProduct(card, state); }
  });
  document.addEventListener('click', event => { if (event.target.tagName === 'DIALOG') { const rect = event.target.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close(); } });
  document.addEventListener('close', () => { if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('goli-dialog-open'); }, true);
  document.addEventListener('scroll', event => { const element = event.target.closest?.('[data-gs-carousel],.discover-section'); carouselStates.get(element)?.reflect(); }, true);
  window.addEventListener('resize', () => { for (const carousel of carousels) carousel.element.isConnected ? carousel.measure() : carousel.destroy(); });
  document.addEventListener('pointerover', event => { const element = event.target.closest?.('[data-gs-carousel]'); if (element && !element.contains(event.relatedTarget) && event.pointerType !== 'touch') carouselStates.get(element)?.hover(true); });
  document.addEventListener('pointerout', event => { const element = event.target.closest?.('[data-gs-carousel]'); if (element && !element.contains(event.relatedTarget)) carouselStates.get(element)?.hover(false); });
  document.addEventListener('focusin', event => { const element = event.target.closest?.('[data-gs-carousel]'); carouselStates.get(element)?.focus(true); });
  document.addEventListener('focusout', event => { const element = event.target.closest?.('[data-gs-carousel]'); if (element && !element.contains(event.relatedTarget)) carouselStates.get(element)?.focus(false); });
  document.addEventListener('pointerdown', event => { const element = event.target.closest?.('[data-gs-carousel]'); carouselStates.get(element)?.pointer(true); }, { passive: true });
  document.addEventListener('pointerup', () => { for (const carousel of carousels) carousel.pointer(false); }, { passive: true });
  document.addEventListener('pointercancel', () => { for (const carousel of carousels) carousel.pointer(false); }, { passive: true });
  document.addEventListener('wheel', event => { const element = event.target.closest?.('[data-gs-carousel]'); carouselStates.get(element)?.interact(); }, { passive: true });
  document.addEventListener('visibilitychange', () => { for (const carousel of carousels) carousel.resume(); });
  reducedMotion.addEventListener?.('change', () => { for (const carousel of carousels) carousel.resume(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { for (const carousel of carousels) if (event.target === carousel.element || event.target.contains(carousel.element)) carousel.destroy(); });
  window.addEventListener('pageshow', event => { if (event.persisted) serialize(readCart).catch(error => notify(error.message)); });
  window.GoliCommerce = { init, selection, addPayload, serialize, money, endpoint, changeLine, readCart, setCheckoutHandler(handler) { checkoutHandler = typeof handler === 'function' ? handler : null; } };
  document.dispatchEvent(new CustomEvent('goli:commerce-ready'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); serialize(readCart).catch(error => notify(error.message)); }, { once: true });
  else { init(); serialize(readCart).catch(error => notify(error.message)); }
})();
